import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Search, UserX, Users } from 'lucide-react';
import { useStickyScroll } from '@aimsos/hooks';
import { useTranslation } from '@aimsos/i18n';
import { Button, Pagination, StatusMessage } from '@aimsos/ui';

import PeopleAccessHeader from '../components/PeopleAccessHeader';
import PeopleAccessTabs from '../components/PeopleAccessTabs';
import ConfirmActionModal from '../components/member-detail/ConfirmActionModal';
import EditEmailModal from '../components/member-detail/EditEmailModal';
import ImportedBatchBanner from '../components/members/ImportedBatchBanner';
import MembersBulkBar from '../components/members/MembersBulkBar';
import MembersFilters from '../components/members/MembersFilters';
import MembersFiltersSlideout from '../components/members/MembersFiltersSlideout';
import MembersHeaderActions from '../components/members/MembersHeaderActions';
import MembersIndicators from '../components/members/MembersIndicators';
import MembersRemoveModal from '../components/members/MembersRemoveModal';
import MembersSuspendModal from '../components/members/MembersSuspendModal';
import MembersTable from '../components/members/MembersTable';
import NotInvitedBanner from '../components/members/NotInvitedBanner';
import SendInvitationsConfirm from '../components/members/SendInvitationsConfirm';
import UndoImportConfirm from '../components/members/UndoImportConfirm';
import { MEMBER_STATUS } from '../constants/copy';
import { MEMBER_BULK_ACTION } from '../constants/memberActions';
import { MEMBERS_PAGE_SIZE_OPTIONS } from '../constants/membersFilters';
import { useImportBatchBanner } from '../hooks/useImportBatchBanner';
import { useImportBatchUrlSync, useInitialImportBatch } from '../hooks/useImportBatchNavigation';
import { usePermissions } from '../hooks/usePermission';
import { useMemberRowActions } from '../hooks/useMemberRowActions';
import { useMemberFilterOptions } from '../hooks/useMemberFilterOptions';
import { useMembers } from '../hooks/useMembers';
import { useMemberSelection } from '../hooks/useMemberSelection';
import { useMembersExport } from '../hooks/useMembersExport';
import { useMembersFilters } from '../hooks/useMembersFilters';
import { useCompressAllowed } from '../hooks/useCompressAllowed';
import { useMemberStats } from '../hooks/useMemberStats';
import { useNotInvitedBanner } from '../hooks/useNotInvitedBanner';
import { useOwnPeopleAccessHeader } from '../hooks/useOwnPeopleAccessHeader';
import { formatBatchLabel } from '../utils/formatBatchLabel';
import { canSelectMembers } from '../utils/memberActionRules';
import { withAppliedBatch } from '../utils/withAppliedBatch';

/**
 * Members tab (list-page.mdc): a pinned band with the title and actions (it compresses on scroll),
 * then the tabs, indicators and filters, the results and a pinned pagination footer. The page takes the shared header over
 * from `PeopleAccessLayout` (`useOwnPeopleAccessHeader`), whose `<main>` is the one scroll
 * container, so the sticky behaviour reads that scroller.
 */
function MembersListPage() {
  const { t, i18n } = useTranslation('people-access');
  useOwnPeopleAccessHeader();
  const initialBatch = useInitialImportBatch();
  const filters = useMembersFilters({ importBatch: initialBatch.batchId });
  useImportBatchUrlSync(filters.importBatch);
  const { openDraft, setPage, setPageSize, clearAll } = filters;
  const members = useMembers(filters, { onPageOverflow: setPage });
  const stats = useMemberStats();
  const banner = useNotInvitedBanner();
  const batchBanner = useImportBatchBanner(filters.importBatch);
  const loadedOptions = useMemberFilterOptions();
  // An applied batch always has an option, so its chip and slideout control never go missing.
  const options = useMemo(
    () => ({
      ...loadedOptions,
      batches: withAppliedBatch(loadedOptions.batches, filters.importBatch, t('members.filters.imported')),
    }),
    [loadedOptions, filters.importBatch, t],
  );
  const { actor, mode } = usePermissions();
  // Without any bulk action to run, rows are not selectable and no bar ever shows.
  const canSelect = canSelectMembers(actor, { mode });
  const selection = useMemberSelection(members.rows);
  const { clear: clearSelection, deselect } = selection;
  const actions = useMemberRowActions({ clearSelection, deselect });
  const { exportCsv } = useMembersExport();
  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);

  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  useLayoutEffect(() => {
    scrollRef.current = rootRef.current?.closest('main') ?? null;
  }, []);
  const sticky = useStickyScroll(scrollRef);
  // A list too short to absorb the compression keeps its pinned header expanded (see the hook).
  const compressAllowed = useCompressAllowed(scrollRef, sticky.isScrolled);
  const isCompressed = sticky.isScrolled && compressAllowed;
  // The pinned band is painted whenever the page has scrolled under it, compressed or not, so the
  // rows never show through it.
  const isStuck = sticky.isScrolled;

  // A new element every render would defeat the memo of `PeopleAccessHeader`.
  const headerActions = useMemo(
    () => <MembersHeaderActions onExport={exportCsv} size={isCompressed ? 'sm' : 'md'} />,
    [exportCsv, isCompressed],
  );

  // The selection survives paging but never outlives the query it was made in: members the new
  // filters hide are dropped.
  const { search, status, role, group, mfa, added, importBatch } = filters;
  useEffect(() => {
    clearSelection();
  }, [search, status, role, group, mfa, added, importBatch, clearSelection]);

  const handleOpenAllFilters = useCallback(() => {
    openDraft();
    setIsAllFiltersOpen(true);
  }, [openDraft]);
  const handleCloseAllFilters = useCallback(() => setIsAllFiltersOpen(false), []);
  const { runBulkAction, runRowAction } = actions;
  // Whatever the row action did, that member leaves the selection (it may drop out of the filter).
  // An action that only opened a question (`null`: Suspend, Edit email) leaves it to the answer.
  const handleRowAction = useCallback(
    async (actionId, member) => {
      const result = await runRowAction(actionId, member);
      if (result !== null) deselect([member.id]);
    },
    [runRowAction, deselect],
  );
  // "Send all invitations" of the banner: everyone not invited, checked in the confirmation first.
  const { fetchAll: fetchAllNotInvited, send: sendBannerInvitation } = banner;
  const [loadingAll, setLoadingAll] = useState(false);
  const loadingAllRef = useRef(false);
  const handleSendAllNotInvited = useCallback(async () => {
    if (loadingAllRef.current) return;
    loadingAllRef.current = true;
    setLoadingAll(true);
    try {
      await runBulkAction(MEMBER_BULK_ACTION.SEND_INVITATIONS, await fetchAllNotInvited());
    } catch {
      // The banner stays and offers it again.
    } finally {
      loadingAllRef.current = false;
      setLoadingAll(false);
    }
  }, [fetchAllNotInvited, runBulkAction]);
  const handleBulkAction = useCallback(
    (actionId) => runBulkAction(actionId, selection.selectedMembers),
    [runBulkAction, selection.selectedMembers],
  );
  // "Select the N not invited" of the import batch: they may be on other pages.
  const { select: selectMembers } = selection;
  const queryKey = JSON.stringify([search, status, role, group, mfa, added, importBatch]);
  const queryKeyRef = useRef(queryKey);
  useLayoutEffect(() => {
    queryKeyRef.current = queryKey;
  }, [queryKey]);
  const handleSelectNotInvited = useCallback(async () => {
    const forQuery = queryKeyRef.current;
    try {
      const people = await batchBanner.fetchNotInvited();
      // The list moved to another batch or filter while this loaded: its people no longer apply.
      if (queryKeyRef.current === forQuery) selectMembers(people);
    } catch {
      // The list and the selection stay as they are; the banner offers it again.
    }
  }, [batchBanner, selectMembers]);
  const [undoOpen, setUndoOpen] = useState(false);
  const closeUndo = useCallback(() => setUndoOpen(false), []);
  const { setFilter } = filters;
  const confirmUndo = useCallback(async () => {
    if (await batchBanner.undo()) {
      setUndoOpen(false);
      setFilter('importBatch', '');
    }
  }, [batchBanner, setFilter]);
  const handlePrevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), [setPage]);
  const handleNextPage = useCallback(() => setPage((p) => p + 1), [setPage]);

  // New badges only while the batch the import landed with is still the applied one.
  const newBatchId =
    initialBatch.autoAppliedBatchId && filters.importBatch === initialBatch.autoAppliedBatchId
      ? initialBatch.autoAppliedBatchId
      : '';

  const { rows, total, isLoading, error, isEmptyCatalog, isNoResults, refetch } = members;
  const showFooter = !error && (total > 0 || isLoading);

  const paginationLabels = useMemo(
    () => ({
      rowsPerPage: t('members.list.pagination.rowsPerPage'),
      items: t('members.list.pagination.items', { range: members.rangeLabel, total }),
      previousPage: t('members.list.pagination.previousPage'),
      nextPage: t('members.list.pagination.nextPage'),
    }),
    [t, members.rangeLabel, total],
  );

  const renderResults = () => {
    if (error) {
      return (
        <div className="flex flex-col items-center">
          <StatusMessage icon={AlertTriangle} title={t('members.list.loadError')} subtitle={t('members.list.retryHint')} />
          <Button variant="secondary" size="md" onClick={refetch}>
            {t('members.stats.retry')}
          </Button>
        </div>
      );
    }
    if (isEmptyCatalog) {
      return (
        <StatusMessage
          icon={Users}
          title={t('members.list.empty.title')}
          subtitle={t('members.list.empty.subtitle')}
        />
      );
    }
    if (isNoResults) {
      return (
        <div className="flex flex-col items-center">
          <StatusMessage
            icon={Search}
            title={t('members.list.noResults.title')}
            subtitle={t('members.list.noResults.subtitle')}
          />
          <Button variant="secondary" size="md" onClick={clearAll}>
            {t('members.filters.clear')}
          </Button>
        </div>
      );
    }
    return (
      <MembersTable
        rows={rows}
        isLoading={isLoading}
        newBatchId={newBatchId}
        selection={canSelect ? selection : undefined}
        onRowAction={handleRowAction}
      />
    );
  };

  return (
    <div ref={rootRef} className="flex flex-1 flex-col">
      {/* Only the title and the buttons stay pinned (like /workspaces); the tabs, the indicators and
          the filters below scroll away with the content. */}
      <div className="sticky top-0 z-20">
        {/* Padding lives on the painted element, never on the sticky wrapper. */}
        <div
          className={['pb-3 pt-2 transition-colors duration-200', isStuck ? 'bg-surface-base' : '']
            .filter(Boolean)
            .join(' ')}
        >
          <PeopleAccessHeader
            showTabs={false}
            size={isCompressed ? 'compress' : 'M'}
            actions={headerActions}
          />
        </div>
        {isStuck ? (
          <div
            aria-hidden
            className="pointer-events-none h-6 bg-gradient-to-b from-surface-base to-transparent"
          />
        ) : null}
      </div>

      <div className="flex flex-col gap-4 pb-4 pt-1">
        <PeopleAccessTabs />
        <MembersIndicators
          stats={stats.stats}
          isLoading={stats.isLoading}
          error={stats.error}
          onRetry={stats.refetch}
        />
        {batchBanner.batch ? (
          <ImportedBatchBanner
            batch={batchBanner.batch}
            onSelectNotInvited={handleSelectNotInvited}
            onUndo={() => setUndoOpen(true)}
          />
        ) : null}
        {/* The batch banner already says who is not invited: the single-member one steps aside. */}
        {banner.isVisible && !batchBanner.batch ? (
          <NotInvitedBanner
            member={banner.member}
            count={banner.count}
            onSend={banner.count > 1 ? handleSendAllNotInvited : sendBannerInvitation}
            busy={loadingAll || banner.sending}
            onDismiss={banner.dismiss}
          />
        ) : null}
        <MembersFilters filters={filters} options={options} onAllFiltersClick={handleOpenAllFilters} />
      </div>

      {/* No bottom padding while the footer shows: it would un-pin it at the end of the scroll. */}
      <div className={['flex min-w-0 flex-1 flex-col gap-4', showFooter ? '' : 'pb-6'].join(' ')}>
        <div data-testid="members-results" className="flex min-w-0 flex-col gap-3">
          <MembersBulkBar
            members={canSelect ? selection.selectedMembers : []}
            onAction={handleBulkAction}
            onClear={clearSelection}
            disabled={actions.isBusy}
          />
          {renderResults()}
        </div>

        {showFooter ? (
          <div className="sticky bottom-0 z-10 mt-auto bg-surface-base pb-6 pt-2">
            <Pagination
              rangeLabel={members.rangeLabel}
              totalItems={total}
              currentPage={members.page}
              totalPages={members.totalPages}
              hasPrevious={members.hasPrevious}
              hasNext={members.hasNext}
              perPage={members.pageSize}
              pageSizeOptions={MEMBERS_PAGE_SIZE_OPTIONS}
              onPrevPage={handlePrevPage}
              onNextPage={handleNextPage}
              onPageSizeChange={setPageSize}
              disabled={isLoading}
              labels={paginationLabels}
            />
          </div>
        ) : null}
      </div>

      <ConfirmActionModal
        isOpen={Boolean(actions.pendingSuspendRow)}
        onClose={actions.cancelSuspendRow}
        title={t('memberDetail.confirm.suspend.title', { name: actions.pendingSuspendRow?.displayName ?? '' })}
        message={t('memberDetail.confirm.suspend.message')}
        confirmLabel={t('memberDetail.confirm.suspend.confirm')}
        confirmVariant="danger"
        icon={UserX}
        iconTone="Alert"
        onConfirm={actions.confirmSuspendRow}
      />

      {actions.pendingEmail ? (
        <EditEmailModal
          memberId={actions.pendingEmail.id}
          email={actions.pendingEmail.email}
          resends={actions.pendingEmail.status !== MEMBER_STATUS.NOT_INVITED}
          onClose={actions.cancelEmail}
          onSave={actions.saveEmail}
        />
      ) : null}

      {undoOpen && batchBanner.batch ? (
        <UndoImportConfirm
          label={formatBatchLabel(t, i18n.language, new Date(batchBanner.batch.date), batchBanner.batch.file)}
          count={batchBanner.batch.memberCount}
          isBusy={batchBanner.isUndoing}
          onConfirm={confirmUndo}
          onClose={closeUndo}
        />
      ) : null}

      {actions.pendingInvite.length > 0 ? (
        <SendInvitationsConfirm
          members={actions.pendingInvite}
          isBusy={actions.isBusy}
          onConfirm={actions.confirmInvite}
          onClose={actions.cancelInvite}
        />
      ) : null}

      <MembersSuspendModal
        members={actions.pendingSuspend}
        isBusy={actions.isBusy}
        onConfirm={actions.confirmSuspend}
        onCancel={actions.cancelSuspend}
      />

      <MembersRemoveModal
        members={actions.pendingRemove}
        isBusy={actions.isBusy}
        onConfirm={actions.confirmRemove}
        onCancel={actions.cancelRemove}
      />

      {/* Overlays are siblings of the scrolling content, never inside the sticky band. */}
      <MembersFiltersSlideout
        isOpen={isAllFiltersOpen}
        onClose={handleCloseAllFilters}
        draft={filters.draft}
        setDraftField={filters.setDraftField}
        applyDraft={filters.applyDraft}
        clearDraft={filters.clearDraft}
        options={options}
      />
    </div>
  );
}

export default MembersListPage;
